import { StageMatrixExtraData } from '../../../models/end-client-reporting-component-types/stage-matrix';
import { StageExplainerExtraData } from '../../../models/end-client-reporting-component-types/stage-explainer';

type Panel = StageExplainerExtraData['panels'][number];

/**
 * An explainer's panels, read off the ladder it explains.
 *
 * The two face each other in a deck -- one teaches the columns the other charts -- so the ladder
 * owns the structure and the names, and the explainer owns only the prose. A stage becomes a panel,
 * the rungs it spans become that panel's parts, and the ladder's names for those rungs become the
 * part headings. `block-examples.ts` already did this once at seed time, with the note that "two
 * hand-kept lists of eight is exactly the pair that drifts"; this is the same derivation made
 * permanent and pointed the other way.
 *
 * `stored` is the explainer's own saved panels, and the only thing taken from it is the bullet
 * points -- matched by stage index and part index, because a stage renamed on the ladder is still
 * the same stage and should keep the prose written under it. A stage that has moved takes its
 * bullets with it only if it moved on the ladder too, which is where stages are reordered.
 */
export function panelsFromLadder(
  ladder: StageMatrixExtraData,
  stored: Panel[] | undefined,
  drawnRungs: readonly string[],
): Panel[] {
  const rungNames = ladder.rungNames ?? [];
  let at = 0;

  return (ladder.stages ?? [])
    .map((stage, index) => {
      const span = Math.max(0, Math.trunc(stage.rungs));
      // Sliced out of the rungs the ladder actually drew, so a stage claiming answers that are not
      // there gets none -- and each heading falls back to the answer as the survey worded it where
      // the deck has not renamed that rung, which is exactly what the ladder prints.
      const headings = drawnRungs
        .slice(at, at + span)
        .map((label, offset) => rungNames[at + offset]?.trim() || label);
      at += span;

      const keptParts = stored?.[index]?.parts ?? [];
      return {
        title: stage.label,
        parts: headings.map((heading, part) => ({
          heading,
          points: keptParts[part]?.points ?? [],
        })),
      };
    })
    // Only the stages the ladder shows. `rungsFor` stops once the rungs run out, so a stage past
    // the end of the scale is drawn on neither -- widening three stages until they cover every
    // answer leaves a fourth claiming rungs that are not there, and it was still getting a panel.
    .filter(panel => (panel.parts ?? []).length > 0);
}

/**
 * The whole derived settings object for a paired explainer: the ladder's stages as panels, and the
 * ladder's own band, which is the one field the two modules already declared identically.
 */
export function explainerFromLadder(
  ladder: StageMatrixExtraData,
  stored: StageExplainerExtraData | undefined,
  drawnRungs: readonly string[],
): StageExplainerExtraData {
  return {
    panels: panelsFromLadder(ladder, stored?.panels, drawnRungs),
    spectrum: ladder.spectrum,
  };
}
